import React, { useState, useRef, useEffect, Fragment } from "react";
import classnames from "classnames";
import { fabric } from "sunzi-fabric";
import dayjs from "dayjs";
import { useLocal } from "@/hooks";
import { Shop, RGB } from "@/sunzi";
import { VipIncrement } from "@/increment/increment";
import { Layout, Wrapper, Loading, ImageLoad } from "@/components";
import { i18n, uuid } from "@/utils";
import { SpotifyColor, SpotifyTrack } from "./spotify-code";
import { SourceListItem, Type } from "../factory/spotify-code-film";
import spotifySearch from "./request";
import TrackItem from "./track-item";
import Icon from "../icon";
import NumLoading from "../loading";
import styles from "./style.less";
import GiftDrawer from "../giftdrawer";

interface SpotifyCodeProps {
  /** 店铺信息 */
  shop: Shop;
  /** 主题色 */
  theme?: RGB;
  /** 默认颜色 */
  defaultColor?: SpotifyColor;
  /** 推荐歌曲 */
  recommendTracks?: SpotifyTrack[];
  /** ga统计 */
  gtag: any;
  /** 确认回调 */
  handleConfirm: (vipIncrement?: VipIncrement, giftBox?: Type) => void;
  /**唱片类型 */
  filmStyle?: string;
  /**照片上传失败 */
  uploadError: boolean;
  showSpotify: boolean;
  /**上传进度条 */
  percent: number;
  sourceList: SourceListItem[];
  hendleCloseSpotify: () => void;
  hendleSpotifyCart: () => void;
  /**设置回调函数 */
  getCurrentTrackCb: (value: any) => void;
  _renderContent: React.ReactNode;
  reStart: boolean;
  /** 礼盒数据 */
  giftBoxs?: Type[];
}

const SpotifyCode: React.FC<SpotifyCodeProps> = ({
  shop,
  theme = {
    r: 29,
    g: 184,
    b: 84,
  },
  gtag,
  defaultColor,
  recommendTracks = [],
  handleConfirm,
  filmStyle,
  _renderContent,
  hendleCloseSpotify,
  hendleSpotifyCart,
  getCurrentTrackCb,
  uploadError,
  showSpotify,
  reStart,
  percent,
  sourceList,
  giftBoxs,
}) => {
  // 设置国际化
  useLocal(shop.language);
  /** 输入框实例 */
  const _input = useRef<HTMLInputElement>(null);
  /** 搜索列表实例 */
  const _list = useRef<HTMLDivElement>(null);
  /** 搜索结果游标 */
  const _pagination = useRef<number>(0);
  /** 搜索结果总数 */
  const _total = useRef<number>(0);
  /** 搜索结果key*/
  const _listKey = useRef<string>();
  /** 搜索内容 */
  const [searchValue, setSearchValue] = useState<string>("");
  /** 搜索加载状态 */
  const [searchLoading, setSearchLoading] = useState<boolean>();
  /** 搜索加载更多状态 */
  const [searchLoadmore, setSearchLoadmore] = useState<boolean>();
  /** 搜索加载状态 */
  const [tracks, setTracks] = useState<SpotifyTrack[]>();
  /** 滚动渐变 */
  const [scrollGradient, setScrollGradient] = useState<boolean[]>([]);
  /** 当前激活的歌曲 */
  const [currentTrack, setCurrentTrack] = useState<SpotifyTrack>();
  /** 自定义演奏者 */
  const [artistsValue, setArtistsValue] = useState<string>("");
  /** 上传显隐 */
  const [uploadVisible, setUploadVisible] = useState<boolean>(false);
  /** 礼盒弹窗显隐 */
  const [showGiftDrawer, setShowGiftDrawer] = useState<boolean>(false);
  /** 保存失败时的vip */
  const [vipIncrement, setVipIncrement] = useState<VipIncrement>();
  /** 保存失败时的礼盒 */
  const [giftBox, setGiftBox] = useState<Type>();

  useEffect(() => {
    hendleSpotifyCart();
  }, []);

  useEffect(() => {
    gtag("event", "tap_change_color");
  }, [defaultColor]);

  // 有歌无礼盒直接加车，有礼盒显礼盒
  useEffect(() => {
    if (currentTrack && giftBoxs && giftBoxs.length > 0) {
      setShowGiftDrawer(true);
    } else if (currentTrack) {
      handleConfirm();
    }
  }, [currentTrack, giftBoxs]);

  useEffect(() => {
    if (currentTrack) {
      setArtistsValue(currentTrack.artists);
      gtag("event", "tap_spotify_track", {
        event_label: currentTrack.name,
      });
    }
  }, [currentTrack]);

  /** 监听搜索按键 */
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13) handleSearch();
  };

  /** 搜索监听 */
  const handleSearch = async () => {
    setSearchLoading(true);
    _input.current?.blur();
    // 每次搜索生成一个新的key，强制dom更新
    _listKey.current = uuid();
    await _spotifySearch();
    gtag("event", "tap_search_track", {
      event_label: searchValue,
    });
    setSearchLoading(false);
  };

  /** spotify api 搜索 */
  const _spotifySearch = async (pagination: number = 0) =>
    spotifySearch(searchValue, pagination).then((response) => {
      if (response.status && response.data) {
        setTracks(
          pagination === 0
            ? response.data.tracks
            : (tracks || []).concat(response.data.tracks)
        );
        _total.current = response.data.total;
        _pagination.current = pagination;
        if (_list.current) {
          const { scrollHeight, offsetHeight } = _list.current;
          setScrollGradient([false, scrollHeight > offsetHeight]);
        }
      } else {
        gtag("event", "tap_search_error");
      }
    });

  /** 搜索列表滚动监听 */
  const handleTrackListScroll = async () => {
    if (_list.current) {
      const { scrollTop, scrollHeight, offsetHeight } = _list.current;
      setScrollGradient([
        scrollTop > 100,
        scrollHeight - scrollTop - offsetHeight > 100,
      ]);
      if (
        scrollHeight - scrollTop - offsetHeight <= 80 &&
        !searchLoadmore &&
        (tracks?.length ?? 0) < _total.current
      ) {
        setSearchLoadmore(true);
        await _spotifySearch(_pagination.current + 1);
        setSearchLoadmore(false);
      }
    }
  };
  const handleCurrentTrack = (item: any) => {
    setCurrentTrack(item);
    getCurrentTrackCb(item);
  };

  /** content渲染 */
  const renderContent = () => {
    if (tracks === undefined)
      return (
        <div className={classnames(styles.trackListWrapper, styles.recommend)}>
          <div className={styles.recommendTitle}>
            {i18n.format("modules.spotify.code.film.popular")}
          </div>
          {/* 热门歌曲列表 */}
          <div className={styles.trackList}>
            {recommendTracks.map((item, index) => (
              <TrackItem
                key={index}
                track={item}
                onClick={() => handleCurrentTrack(item)}
              />
            ))}
          </div>
        </div>
      );
    else if (tracks.length > 0)
      return (
        <div className={classnames(styles.trackListWrapper, styles.result)}>
          <div className={styles.resultTitle}>
            {i18n.format("modules.spotify.code.search.reuslt")}
          </div>
          <div
            ref={_list}
            key={_listKey.current}
            className={styles.trackList}
            onScrollCapture={handleTrackListScroll}
          >
            {tracks.map((item, index) => (
              <TrackItem
                key={index}
                track={item}
                onClick={() => handleCurrentTrack(item)}
              />
            ))}
            {tracks.length < _total.current && (
              <div className={styles.loadmore}>
                <Loading size={20} />
              </div>
            )}
          </div>
          <Wrapper className={styles.trackListGradient}>
            <div
              className={classnames({
                [styles.topGradient]: scrollGradient[0],
              })}
            />
            <div
              className={classnames({
                [styles.bottomGradient]: scrollGradient[1],
              })}
            />
          </Wrapper>
        </div>
      );
    else
      return (
        <div className={classnames(styles.trackListWrapper, styles.result)}>
          <div className={styles.resultTitle}>
            {i18n.format("modules.spotify.code.film.search.reuslt")}
          </div>
          <div className={classnames(styles.trackList, styles.trackListEmpty)}>
            <img src={require("@/assets/search-no-result.png")} />
            <span> {i18n.format("modules.spotify.code.film.no.result")}</span>
          </div>
        </div>
      );
  };
  /**关闭加车页面 */
  const hendleCloseCart = () => {
    if (!reStart) {
      setCurrentTrack(undefined);
    }
  };
  /**重新加车 */
  const handleReset = () => {
    if (!reStart) {
      handleConfirm(vipIncrement, giftBox);
    }
  };

  // 关闭礼盒弹窗
  const handleClose = () => {
    setShowGiftDrawer(false);
  };

  const handleValidateConfirm = (
    vipIncrement?: VipIncrement,
    giftBox?: Type
  ) => {
    setShowGiftDrawer(false);
    setVipIncrement(vipIncrement);
    setGiftBox(giftBox);
    handleConfirm(vipIncrement, giftBox);
  };
  return (
    <Layout theme={theme} plugins={[]}>
      <div className="sunzi-spotify-code">
        <div className={styles.topWrapper}>
          <div className={styles.iconLeft} onClick={hendleCloseSpotify}>
            <Icon type="icon-left" size={25} color="#fff" />
          </div>
          <div
            className={classnames(styles.inputWrapper, {
              [styles.inputFocusWrapper]: searchValue,
            })}
          >
            <div className={styles.searchWrapper}>
              <Icon type="search" size={30} />
            </div>
            <input
              ref={_input}
              value={searchValue}
              placeholder={i18n.format("modules.spotify.code.film.placeholder")}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
            />
            <div className={styles.confirm} onClick={handleSearch}>
              {searchLoading ? (
                <Loading size={24} color="#C0C0C3" />
              ) : (
                <div className={styles.iconWrapper}>
                  <Icon type="search" size={30} />
                </div>
              )}
            </div>
          </div>
        </div>
        {renderContent()}
        {currentTrack && (
          <Wrapper className={styles.preview}>
            <Wrapper
              className={styles.previewWrapper}
              style={{
                backgroundImage: `url(${currentTrack?.album})`,
              }}
            ></Wrapper>
            <Wrapper className={styles.previewContent}>
              <div className={styles.previewTop}>
                <div className={styles.icon} onClick={hendleCloseCart}>
                  <Icon type="icon-left" size={25} color="#fff" />
                </div>
              </div>
              <div className={styles.previewMain}>
                <div className={styles.previewFilm}>{_renderContent}</div>
                {reStart ? (
                  <div className={styles.text}>
                    <div className={styles.currentTrackName}>
                      {currentTrack.name}
                    </div>
                    <div className={styles.currentTrackArtists}>
                      {currentTrack.artists}
                    </div>
                  </div>
                ) : (
                  <div className={styles.reset}>
                    <div className={styles.resetWrapper}></div>
                    <div className={styles.resetBox} onClick={handleReset}>
                      <div className={styles.icon}>
                        <Icon type="reset" size={25} color="#F56C6C" />
                      </div>
                      <div className={styles.error}>Network Error!</div>
                      <div className={styles.again}>Try again</div>
                    </div>
                  </div>
                )}
                <div className={styles.cartLoad}>
                  <div className={styles.previewCd}>
                    <div className={styles.previewAlbum}>
                      <ImageLoad
                        source={currentTrack?.album}
                        className={styles.albumImg}
                      />
                      <div className={styles.album}>
                        <img
                          src={require(`@/assets/spotify-code-film/album.png`)}
                        />
                      </div>
                    </div>
                    <div className={styles.cdBox}>
                      <div className={styles.albumCd}>
                        {reStart ? (
                          <div>
                            <div className={styles.albumBox}>
                              {/* 默认边框 */}
                              <div className={styles.leftWrapper}>
                                <div
                                  className={classnames(styles.leftBorder)}
                                />
                              </div>
                              <div className={styles.rightWrapper}>
                                <div
                                  className={classnames(styles.rightBorder)}
                                />
                              </div>
                              {/* 默认边框end */}
                              <div className={styles.albumCdImg}>
                                <img
                                  src={require(`@/assets/spotify-code-film/album-cd.png`)}
                                />
                              </div>
                              <div className={styles.mask}>
                                <div
                                  className={classnames(styles.maskBg, {
                                    [styles.maskGreen]: filmStyle == "green",
                                  })}
                                ></div>
                                <div className={styles.numLoading}>
                                  <NumLoading
                                    showSpotify={showSpotify}
                                    reStart={reStart}
                                    percent={percent}
                                  />
                                </div>
                              </div>
                              {/* 内圆 */}
                              <div className={styles.leftWrapper}>
                                <div
                                  className={classnames({
                                    [styles.leftGreen]: filmStyle == "green",
                                    [styles.leftCircle]: filmStyle != "green",
                                    [styles.reSatrtLeft]: reStart,
                                  })}
                                />
                              </div>
                              <div className={styles.rightWrapper}>
                                <div
                                  className={classnames({
                                    [styles.rightGreen]: filmStyle == "green",
                                    [styles.rightCircle]: filmStyle != "green",
                                    [styles.reSatrtRight]: reStart,
                                  })}
                                />
                              </div>
                              {/* 内圆end */}
                            </div>
                            <div
                              className={classnames(
                                styles.albumBox,
                                styles.out
                              )}
                            >
                              {/*  外圆*/}
                              <div className={styles.leftWrapper}>
                                <div
                                  className={classnames(
                                    styles.hide,
                                    styles.leftCircle,
                                    {
                                      [styles.reSatrtLeft]: reStart,
                                    }
                                  )}
                                >
                                  <div
                                    className={classnames(styles.leftSpot, {
                                      [styles.shadowGreen]:
                                        filmStyle == "green",
                                      [styles.reLeftSpot]: reStart,
                                    })}
                                  ></div>
                                </div>
                              </div>
                              <div className={styles.rightWrapper}>
                                <div
                                  className={classnames(
                                    styles.hide,
                                    styles.rightCircle,
                                    {
                                      [styles.reSatrtRight]: reStart,
                                    }
                                  )}
                                >
                                  {/* 点 */}
                                  <div
                                    className={classnames(styles.rightSpot, {
                                      [styles.shadowGreen]:
                                        filmStyle == "green",
                                      [styles.reRightSpot]: reStart,
                                    })}
                                  ></div>
                                </div>
                              </div>
                              {/* 外圆end */}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className={styles.albumBox}>
                              {/* 默认边框 */}
                              <div className={styles.leftWrapper}>
                                <div
                                  className={classnames(styles.leftBorder)}
                                />
                              </div>
                              <div className={styles.rightWrapper}>
                                <div
                                  className={classnames(styles.rightBorder)}
                                />
                              </div>
                              <div className={styles.albumCdImg}>
                                <img
                                  src={require(`@/assets/spotify-code-film/album-cd.png`)}
                                />
                              </div>
                              <div className={styles.mask}>
                                <div
                                  className={classnames(styles.maskBg, {
                                    [styles.maskGreen]: filmStyle == "green",
                                  })}
                                ></div>
                                <div className={styles.numLoading}>
                                  <NumLoading
                                    showSpotify={showSpotify}
                                    reStart={reStart}
                                    percent={percent}
                                  />
                                </div>
                              </div>
                              {/* 内圆 */}
                              <div className={styles.leftWrapper}>
                                <div
                                  className={classnames({
                                    [styles.leftGreen]: filmStyle == "green",
                                    [styles.leftCircle]: filmStyle != "green",
                                    [styles.animationEndLeft]: !reStart,
                                  })}
                                />
                              </div>
                              <div className={styles.rightWrapper}>
                                <div
                                  className={classnames({
                                    [styles.rightGreen]: filmStyle == "green",
                                    [styles.rightCircle]: filmStyle != "green",
                                    [styles.animationEndRight]: !reStart,
                                  })}
                                />
                              </div>
                              {/* 内圆end */}
                              {/* 点 */}
                              <div
                                className={classnames(styles.rightSpot, {
                                  [styles.shadowGreen]: filmStyle == "green",
                                  [styles.rightSpotEnd]: !reStart,
                                })}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Wrapper>
          </Wrapper>
        )}
        {uploadVisible && (
          <Wrapper className={styles.uploadWrapper}>
            <Loading.Line />
          </Wrapper>
        )}
        {/* 礼盒弹窗 */}
        {showGiftDrawer && giftBoxs && (
          <Wrapper>
            <GiftDrawer
              shop={shop!}
              giftBoxs={giftBoxs}
              mask
              currencySymbol={shop!.currencySymbol}
              onClose={handleClose}
              onConfirm={handleValidateConfirm}
            />
          </Wrapper>
        )}
      </div>
    </Layout>
  );
};
export const formatTime = (time: number) =>
  dayjs(time).subtract(1, "s").format("mm:ss");
export default SpotifyCode;
